/**
 * Purchase Tracking System
 * Tracks purchases from Stripe and links them to user profiles
 * Integrates with Google Analytics AND PostHog
 */

import { getCurrentUserProfile, updateConversionStage } from "./userIdentification";
import {
  trackConversionPostHog,
  identifyUserPostHog,
  setUserPropertiesPostHog,
  trackPurchaseWithRevenuePostHog,
} from "./posthogAnalytics";
import { fetchStripeSessionDetails } from "./stripeSessionHelper";

declare global {
  interface Window {
    posthog?: {
      capture: (eventName: string, properties?: Record<string, unknown>) => void;
      identify: (userId: string, properties?: Record<string, unknown>) => void;
      setPersonProperties: (properties: Record<string, unknown>) => void;
    };
  }
}

export interface StripePurchaseData {
  // From Stripe URL parameters
  sessionId?: string;
  checkoutSessionId?: string;
  paymentIntent?: string;
  
  // Purchase metadata
  purchaseId: string;
  purchaseDate: string;
  purchaseTimestamp: number;
  
  // User information
  userId: string;
  userRegion: string;
  userCountry?: string;
  deviceFingerprint: string;
  
  // Page information
  returnedToPage: string; // /gracias or /thank-you
  language: "es" | "en";
  
  // Access granted
  instagramAccess: boolean;
  telegramAccess: boolean;
  instagramClickTime?: string;
  telegramClickTime?: string;
  
  // Browser & Device
  userAgent: string;
  platform: string;
  screenResolution: string;
  timezone: string;
  referrer?: string;
}

/**
 * Extract Stripe parameters from URL
 */
function getStripeParamsFromURL(): {
  sessionId?: string;
  checkoutSessionId?: string;
  paymentIntent?: string;
} {
  const urlParams = new URLSearchParams(window.location.search);
  
  return {
    sessionId: urlParams.get("session_id") || undefined,
    checkoutSessionId: urlParams.get("checkout_session_id") || undefined,
    paymentIntent: urlParams.get("payment_intent") || undefined,
  };
}

/**
 * Get browser and device information
 */
function getBrowserDeviceInfo() {
  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    screenResolution: `${screen.width}x${screen.height}`,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    referrer: document.referrer || undefined,
    language: navigator.language,
    cookiesEnabled: navigator.cookieEnabled,
  };
}

/**
 * Initialize purchase tracking on thank you page.
 * When Stripe session_id is present, fetches session details for revenue tracking in PostHog.
 */
export function initializePurchaseTracking(page: "/gracias" | "/thank-you"): StripePurchaseData {
  const userProfile = getCurrentUserProfile();
  const stripeParams = getStripeParamsFromURL();
  const browserInfo = getBrowserDeviceInfo();

  // Update user to purchaser stage
  updateConversionStage("purchaser");

  // Create purchase record
  const purchaseData: StripePurchaseData = {
    sessionId: stripeParams.sessionId,
    checkoutSessionId: stripeParams.checkoutSessionId,
    paymentIntent: stripeParams.paymentIntent,
    purchaseId: `purchase_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    purchaseDate: new Date().toISOString(),
    purchaseTimestamp: Date.now(),
    userId: userProfile?.userId || "unknown",
    userRegion: userProfile?.region || "UNKNOWN",
    userCountry: userProfile?.country,
    deviceFingerprint: userProfile?.deviceFingerprint || "unknown",
    returnedToPage: page,
    language: page === "/gracias" ? "es" : "en",
    instagramAccess: false,
    telegramAccess: false,
    userAgent: browserInfo.userAgent,
    platform: browserInfo.platform,
    screenResolution: browserInfo.screenResolution,
    timezone: browserInfo.timezone,
    referrer: browserInfo.referrer,
  };

  savePurchaseData(purchaseData);
  trackPurchaseWithGA(purchaseData);
  trackPurchaseWithPostHog(purchaseData);

  // Fetch Stripe session details for revenue and send to PostHog (non-blocking)
  const sessionId = purchaseData.sessionId || purchaseData.checkoutSessionId;
  if (sessionId) {
    void fetchStripeSessionDetails(sessionId).then((details) => {
      if (details && typeof window !== "undefined" && window.posthog) {
        const revenue = (details.amount_total ?? 0) / 100;
        const currency = (details.currency ?? "usd").toUpperCase();
        trackPurchaseWithRevenuePostHog(
          purchaseData.purchaseId,
          revenue,
          currency,
          details.metadata?.plan_tier,
          details.metadata?.product_name,
          details.payment_method_types?.[0],
          !!details.subscription,
          details.subscription,
          purchaseData.userId,
          purchaseData.userCountry,
          sessionId,
        );
      }
    });
  }

  if (import.meta.env.DEV) {
    console.log("🛒 Purchase Tracking Initialized:", purchaseData);
  }

  return purchaseData;
}

/**
 * Save purchase data to localStorage
 */
function savePurchaseData(purchaseData: StripePurchaseData) {
  // Store current purchase
  localStorage.setItem("current_purchase", JSON.stringify(purchaseData));
  
  // Store in purchase history
  const historyKey = "purchase_history";
  const existingHistory = localStorage.getItem(historyKey);
  let history: StripePurchaseData[] = [];
  
  if (existingHistory) {
    try {
      history = JSON.parse(existingHistory);
    } catch (error) {
      console.error("Error parsing purchase history:", error);
    }
  }
  
  history.push(purchaseData);
  
  // Keep only last 10 purchases
  if (history.length > 10) {
    history = history.slice(-10);
  }
  
  localStorage.setItem(historyKey, JSON.stringify(history));
}

/**
 * Track purchase with Google Analytics
 */
function trackPurchaseWithGA(purchaseData: StripePurchaseData) {
  if (typeof window.gtag !== "function") return;
  
  // Enhanced ecommerce purchase event
  window.gtag("event", "purchase", {
    transaction_id: purchaseData.purchaseId,
    value: 0, // We don't have the value, Stripe handles it
    currency: "USD",
    
    // Custom dimensions
    user_id: purchaseData.userId,
    user_region: purchaseData.userRegion,
    user_country: purchaseData.userCountry,
    language: purchaseData.language,
    stripe_session_id: purchaseData.sessionId,
    stripe_payment_intent: purchaseData.paymentIntent,
    
    // Device info
    device_platform: purchaseData.platform,
    screen_resolution: purchaseData.screenResolution,
    timezone: purchaseData.timezone,
  });
  
  // Track purchase confirmation page view
  window.gtag("event", "page_view", {
    page_title: purchaseData.language === "es" ? "Gracias por tu compra" : "Thank You",
    page_location: window.location.href,
    page_path: purchaseData.returnedToPage,
    user_region: purchaseData.userRegion,
    user_country: purchaseData.userCountry,
  });
}

/**
 * Track purchase with PostHog
 */
function trackPurchaseWithPostHog(purchaseData: StripePurchaseData) {
  if (!window.posthog) return;
  
  // Identify user in PostHog
  identifyUserPostHog(purchaseData.userId, {
    region: purchaseData.userRegion,
    country: purchaseData.userCountry,
    device_fingerprint: purchaseData.deviceFingerprint,
    language: purchaseData.language,
    timezone: purchaseData.timezone,
    platform: purchaseData.platform,
    screen_resolution: purchaseData.screenResolution,
  });
  
  // Track purchase event
  window.posthog.capture('purchase_completed', {
    // Purchase IDs
    purchase_id: purchaseData.purchaseId,
    stripe_session_id: purchaseData.sessionId,
    stripe_checkout_session_id: purchaseData.checkoutSessionId,
    stripe_payment_intent: purchaseData.paymentIntent,
    
    // User info
    user_id: purchaseData.userId,
    user_region: purchaseData.userRegion,
    user_country: purchaseData.userCountry,
    device_fingerprint: purchaseData.deviceFingerprint,
    
    // Page info
    returned_to_page: purchaseData.returnedToPage,
    language: purchaseData.language,
    page_url: window.location.href,
    
    // Timestamps
    purchase_date: purchaseData.purchaseDate,
    purchase_timestamp: purchaseData.purchaseTimestamp,
    
    // Device & Browser
    user_agent: purchaseData.userAgent,
    platform: purchaseData.platform,
    screen_resolution: purchaseData.screenResolution,
    timezone: purchaseData.timezone,
    referrer: purchaseData.referrer,
    
    // Access status (will be updated when user clicks)
    instagram_access: purchaseData.instagramAccess,
    telegram_access: purchaseData.telegramAccess,
  });
  
  // Set user properties for segmentation
  setUserPropertiesPostHog({
    last_purchase_date: purchaseData.purchaseDate,
    last_purchase_id: purchaseData.purchaseId,
    total_purchases: getPurchaseHistory().length,
    conversion_stage: 'purchaser',
    preferred_language: purchaseData.language,
    market_region: purchaseData.userRegion,
  });
  
  if (import.meta.env.DEV) {
    console.log("📊 PostHog: Purchase tracked");
  }
}

/**
 * Track Instagram access click
 */
export function trackInstagramAccessClick(service: "mejores_amigos" | "best_friends") {
  const purchaseData = getCurrentPurchaseData();
  if (!purchaseData) return;
  
  // Update purchase data
  purchaseData.instagramAccess = true;
  purchaseData.instagramClickTime = new Date().toISOString();
  savePurchaseData(purchaseData);
  
  // Track in database
  if (purchaseData.paymentIntent) {
    fetch('/api/track-purchase-access', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId: purchaseData.paymentIntent,
        accessType: 'instagram',
        service: service,
      }),
    }).catch(error => {
      console.error('[purchaseTracking] Failed to track Instagram access in database:', error);
    });
  }
  
  // Update conversion stage
  updateConversionStage("converted");
  
  // Calculate time to click
  const timeToClick = purchaseData.instagramClickTime 
    ? Math.round((new Date(purchaseData.instagramClickTime).getTime() - purchaseData.purchaseTimestamp) / 1000)
    : 0;
  
  // Track with GA
  if (typeof window.gtag === "function") {
    window.gtag("event", "instagram_access", {
      event_category: "Purchase Conversion",
      event_label: service,
      
      purchase_id: purchaseData.purchaseId,
      user_id: purchaseData.userId,
      user_region: purchaseData.userRegion,
      user_country: purchaseData.userCountry,
      language: purchaseData.language,
      time_to_click_seconds: timeToClick,
    });
  }
  
  // Track with PostHog
  if (window.posthog) {
    window.posthog.capture('instagram_access_clicked', {
      // Event details
      service: service,
      access_type: 'best_friends',
      
      // Purchase context
      purchase_id: purchaseData.purchaseId,
      user_id: purchaseData.userId,
      user_region: purchaseData.userRegion,
      user_country: purchaseData.userCountry,
      language: purchaseData.language,
      
      // Timing
      click_timestamp: purchaseData.instagramClickTime,
      time_to_click_seconds: timeToClick,
      purchase_timestamp: purchaseData.purchaseTimestamp,
      
      // Stripe context
      stripe_session_id: purchaseData.sessionId,
      stripe_payment_intent: purchaseData.paymentIntent,
      
      // Page info
      page_path: purchaseData.returnedToPage,
      page_url: window.location.href,
    });
    
    // Update user properties
    setUserPropertiesPostHog({
      conversion_stage: 'converted',
      instagram_access: true,
      last_instagram_access: purchaseData.instagramClickTime,
    });
  }
  
  if (import.meta.env.DEV) {
    console.log("📸 Instagram Access Tracked:", {
      service,
      purchaseId: purchaseData.purchaseId,
      clickTime: purchaseData.instagramClickTime,
      timeToClick: `${timeToClick}s`,
    });
  }
}

/**
 * Track Telegram access click
 */
export function trackTelegramAccessClick(group: "spanish" | "english") {
  const purchaseData = getCurrentPurchaseData();
  if (!purchaseData) return;
  
  // Update purchase data
  purchaseData.telegramAccess = true;
  purchaseData.telegramClickTime = new Date().toISOString();
  savePurchaseData(purchaseData);
  
  // Track in database
  if (purchaseData.paymentIntent) {
    fetch('/api/track-purchase-access', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        paymentIntentId: purchaseData.paymentIntent,
        accessType: 'telegram',
        service: group,
      }),
    }).catch(error => {
      console.error('[purchaseTracking] Failed to track Telegram access in database:', error);
    });
  }
  
  // Update conversion stage
  updateConversionStage("converted");
  
  // Calculate time to click
  const timeToClick = purchaseData.telegramClickTime 
    ? Math.round((new Date(purchaseData.telegramClickTime).getTime() - purchaseData.purchaseTimestamp) / 1000)
    : 0;
  
  // Track with GA
  if (typeof window.gtag === "function") {
    window.gtag("event", "telegram_access", {
      event_category: "Purchase Conversion",
      event_label: `telegram_${group}`,
      
      purchase_id: purchaseData.purchaseId,
      user_id: purchaseData.userId,
      user_region: purchaseData.userRegion,
      user_country: purchaseData.userCountry,
      language: purchaseData.language,
      telegram_group: group,
      time_to_click_seconds: timeToClick,
    });
  }
  
  // Track with PostHog
  if (window.posthog) {
    window.posthog.capture('telegram_access_clicked', {
      // Event details
      telegram_group: group,
      access_type: 'vip_group',
      
      // Purchase context
      purchase_id: purchaseData.purchaseId,
      user_id: purchaseData.userId,
      user_region: purchaseData.userRegion,
      user_country: purchaseData.userCountry,
      language: purchaseData.language,
      
      // Timing
      click_timestamp: purchaseData.telegramClickTime,
      time_to_click_seconds: timeToClick,
      purchase_timestamp: purchaseData.purchaseTimestamp,
      
      // Stripe context
      stripe_session_id: purchaseData.sessionId,
      stripe_payment_intent: purchaseData.paymentIntent,
      
      // Page info
      page_path: purchaseData.returnedToPage,
      page_url: window.location.href,
      
      // Combined access status
      both_accesses_used: purchaseData.instagramAccess && purchaseData.telegramAccess,
    });
    
    // Update user properties
    setUserPropertiesPostHog({
      conversion_stage: 'converted',
      telegram_access: true,
      last_telegram_access: purchaseData.telegramClickTime,
      both_accesses_used: purchaseData.instagramAccess && purchaseData.telegramAccess,
    });
  }
  
  if (import.meta.env.DEV) {
    console.log("💬 Telegram Access Tracked:", {
      group,
      purchaseId: purchaseData.purchaseId,
      clickTime: purchaseData.telegramClickTime,
      timeToClick: `${timeToClick}s`,
      bothAccessed: purchaseData.instagramAccess && purchaseData.telegramAccess,
    });
  }
}

/**
 * Get current purchase data
 */
export function getCurrentPurchaseData(): StripePurchaseData | null {
  const data = localStorage.getItem("current_purchase");
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error("Error parsing purchase data:", error);
    }
  }
  return null;
}

/**
 * Get purchase history
 */
export function getPurchaseHistory(): StripePurchaseData[] {
  const data = localStorage.getItem("purchase_history");
  if (data) {
    try {
      return JSON.parse(data);
    } catch (error) {
      console.error("Error parsing purchase history:", error);
    }
  }
  return [];
}

/**
 * Track time spent on thank you page
 */
export function trackTimeOnThankYouPage() {
  const purchaseData = getCurrentPurchaseData();
  if (!purchaseData) return;
  
  let timeSpent = 0;
  const interval = setInterval(() => {
    timeSpent += 1;
    
    // Track milestones
    if (timeSpent === 10 || timeSpent === 30 || timeSpent === 60 || timeSpent === 120) {
      // GA tracking
      if (typeof window.gtag === "function") {
        window.gtag("event", "time_on_thank_you_page", {
          event_category: "Engagement",
          event_label: `${timeSpent}_seconds`,
          
          purchase_id: purchaseData.purchaseId,
          user_id: purchaseData.userId,
          user_region: purchaseData.userRegion,
          time_spent_seconds: timeSpent,
          
          instagram_accessed: purchaseData.instagramAccess,
          telegram_accessed: purchaseData.telegramAccess,
        });
      }
      
      // PostHog tracking
      if (window.posthog) {
        window.posthog.capture('thank_you_page_milestone', {
          milestone_seconds: timeSpent,
          purchase_id: purchaseData.purchaseId,
          user_id: purchaseData.userId,
          user_region: purchaseData.userRegion,
          user_country: purchaseData.userCountry,
          language: purchaseData.language,
          
          instagram_accessed: purchaseData.instagramAccess,
          telegram_accessed: purchaseData.telegramAccess,
          both_accessed: purchaseData.instagramAccess && purchaseData.telegramAccess,
          
          page_path: purchaseData.returnedToPage,
        });
      }
    }
  }, 1000);
  
  // Clean up on page unload
  const handleUnload = () => {
    clearInterval(interval);
    
    // Get latest purchase data
    const latestPurchaseData = getCurrentPurchaseData();
    if (!latestPurchaseData) return;
    
    // Track final time with GA
    if (typeof window.gtag === "function") {
      window.gtag("event", "thank_you_page_exit", {
        event_category: "Engagement",
        
        purchase_id: latestPurchaseData.purchaseId,
        user_id: latestPurchaseData.userId,
        total_time_seconds: timeSpent,
        instagram_accessed: latestPurchaseData.instagramAccess,
        telegram_accessed: latestPurchaseData.telegramAccess,
      });
    }
    
    // Track final time with PostHog
    if (window.posthog) {
      window.posthog.capture('thank_you_page_exit', {
        total_time_seconds: timeSpent,
        purchase_id: latestPurchaseData.purchaseId,
        user_id: latestPurchaseData.userId,
        user_region: latestPurchaseData.userRegion,
        user_country: latestPurchaseData.userCountry,
        language: latestPurchaseData.language,
        
        // Conversion metrics
        instagram_accessed: latestPurchaseData.instagramAccess,
        telegram_accessed: latestPurchaseData.telegramAccess,
        both_accessed: latestPurchaseData.instagramAccess && latestPurchaseData.telegramAccess,
        neither_accessed: !latestPurchaseData.instagramAccess && !latestPurchaseData.telegramAccess,
        
        // Page info
        page_path: latestPurchaseData.returnedToPage,
        stripe_session_id: latestPurchaseData.sessionId,
      });
    }
  };
  
  window.addEventListener("beforeunload", handleUnload);
  
  return () => {
    clearInterval(interval);
    window.removeEventListener("beforeunload", handleUnload);
  };
}

/**
 * Get comprehensive purchase analytics
 */
export function getPurchaseAnalytics() {
  const purchaseData = getCurrentPurchaseData();
  const userProfile = getCurrentUserProfile();
  const purchaseHistory = getPurchaseHistory();
  
  return {
    currentPurchase: purchaseData,
    userProfile,
    purchaseHistory,
    totalPurchases: purchaseHistory.length,
    
    // Conversion metrics
    hasInstagramAccess: purchaseData?.instagramAccess || false,
    hasTelegramAccess: purchaseData?.telegramAccess || false,
    bothAccessesUsed: purchaseData?.instagramAccess && purchaseData?.telegramAccess,
    
    // Stripe data
    hasStripeSessionId: !!purchaseData?.sessionId,
    hasPaymentIntent: !!purchaseData?.paymentIntent,
  };
}
